import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from '@zg-rentals/react-query5';
import { formatDistanceToNow } from 'date-fns';

import { Box, Menu, Paragraph, Table } from '@zillow/constellation-10';

import {
  numberUtils,
  RentalManagerUserMaintenanceTaskWebApiHandler_list,
  useIsScreenLG_GTE,
  useIsScreenSM_GTE,
  useIsScreenXL_GTE,
  useIsScreenXXL,
  useRmwPaths,
} from '@zg-rentals/rental-manager-env';

import { TASKS_TABLE, USE_TASKS_LIST } from '../constants/TaskConstants';
import { DeleteTaskModal } from './DeleteTaskModal';
import { StatusTag } from './StatusTag';

import { overflowParagraphStyles } from '../styles';
import { TasksTableSkeleton } from './TasksTableSkeleton';

import type { RentalManagerUserMaintenanceTaskDto } from '@zg-rentals/rental-manager-env';

type Props = {
  listingAlias?: string;
  showAddressColumn?: boolean;
  fromExternalApp?: boolean;
  tasksToDisplay?: Array<RentalManagerUserMaintenanceTaskDto>;
  onRowClick?: (taskId: string) => void;
  isLoading?: boolean;
};

export const TasksTable = ({
  listingAlias,
  showAddressColumn = true,
  fromExternalApp = false,
  tasksToDisplay,
  onRowClick,
  isLoading = false,
}: Props) => {
  const history = useHistory();
  const isScreenLG_GTE = useIsScreenLG_GTE();
  const isScreenXL_GTE = useIsScreenXL_GTE();
  const isScreenSM_GTE = useIsScreenSM_GTE();
  const isScreenXXL = useIsScreenXXL();
  const paths = useRmwPaths();

  const tableRef = useRef<HTMLTableElement>(null);

  const [maintenanceTaskIdToDelete, setMaintenanceTaskIdToDelete] = useState('');
  const [idOfMenuPopper, setIdOfMenuPopper] = useState('');

  const { data, isInitialLoading } = useQuery({
    queryKey: [USE_TASKS_LIST],
    queryFn: () =>
      RentalManagerUserMaintenanceTaskWebApiHandler_list({
        includeInProgress: true,
      }),
    staleTime: 1000 * 3,
    enabled: tasksToDisplay && tasksToDisplay.length === 0,
  });

  const isDataLoading = isLoading || isInitialLoading;

  const handleTaskClick = (taskId: string) => {
    if (taskId) {
      if (fromExternalApp && onRowClick) {
        onRowClick(taskId);
      } else {
        history.push(`/rental-manager/maintenance-tasks/task?id=${taskId}`);
      }
    }
  };

  useEffect(() => {
    const closeOpenMenus = (e: MouseEvent) => {
      if (tableRef.current && idOfMenuPopper && !tableRef.current.contains(e.target as Node)) {
        setTimeout(() => {
          setIdOfMenuPopper('');
        }, 0);
      }
    };

    document.addEventListener('mouseup', closeOpenMenus);

    return () => document.removeEventListener('mouseup', closeOpenMenus);
  }, [idOfMenuPopper]);

  const tasksToRender = tasksToDisplay || data?.tasks;

  return (
    <>
      <Table.Root appearance="horizontal" aria-label="Medium-sized horizontal table" size="md" ref={tableRef}>
        {isDataLoading ? (
          <TasksTableSkeleton />
        ) : (
          <>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Name</Table.HeaderCell>
                {isScreenLG_GTE && showAddressColumn && <Table.HeaderCell>Property address</Table.HeaderCell>}
                {isScreenSM_GTE && <Table.HeaderCell>Status</Table.HeaderCell>}

                {isScreenXL_GTE && <Table.HeaderCell>Last updated</Table.HeaderCell>}
                {isScreenXXL && <Table.HeaderCell>Cost</Table.HeaderCell>}
                <Table.HeaderCell> </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {tasksToRender?.map((task, idx) => {
                return (
                  <Table.Row
                    css={{ cursor: 'pointer', verticalAlign: 'middle' }}
                    key={`${task.listingAlias}-${idx}`}
                    onClick={() => handleTaskClick(task.maintenanceTaskId)}
                    interactive={true}
                  >
                    <Table.Cell>
                      <Paragraph textStyle="body-sm" css={overflowParagraphStyles}>
                        {task.name}
                      </Paragraph>

                      {!isScreenLG_GTE && showAddressColumn && (
                        <Paragraph textStyle="fineprint" css={overflowParagraphStyles} color="text.subtle">
                          {task.fullAddress}
                        </Paragraph>
                      )}
                    </Table.Cell>
                    {isScreenLG_GTE && showAddressColumn && (
                      <Table.Cell>
                        <Paragraph textStyle="body-sm" css={overflowParagraphStyles}>
                          {task.fullAddress}
                        </Paragraph>
                      </Table.Cell>
                    )}
                    {isScreenSM_GTE && (
                      <Table.Cell>
                        <Box css={{ display: 'flex', alignItems: 'center' }}>
                          <StatusTag status={task.status} isLoading={isDataLoading} />
                        </Box>
                      </Table.Cell>
                    )}
                    {isScreenXL_GTE && (
                      <Table.Cell>
                        {formatDistanceToNow(new Date(Number(task?.updatedTimestamp)), { addSuffix: true })}
                      </Table.Cell>
                    )}
                    {isScreenXXL && <Table.Cell>{numberUtils.asCurrencyWithDefault(task.cost / 100)}</Table.Cell>}

                    <Table.Cell>
                      <Box
                        onClick={(e: React.MouseEvent<HTMLElement>) => {
                          e.stopPropagation();
                        }}
                        css={{ display: 'flex', justifyContent: 'center' }}
                      >
                        <Menu
                          open={idOfMenuPopper === task.maintenanceTaskId}
                          onOpenChange={(open) => open && setIdOfMenuPopper(task.maintenanceTaskId)}
                          content={
                            <>
                              {fromExternalApp ? (
                                <Menu.Item asChild>
                                  <a href={`${paths.maintenanceTrackerPage}/task?id=${task.maintenanceTaskId}`}>
                                    Edit task
                                  </a>
                                </Menu.Item>
                              ) : (
                                <Menu.Item
                                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                                    e.stopPropagation();
                                    handleTaskClick(task.maintenanceTaskId);
                                  }}
                                >
                                  Edit task
                                </Menu.Item>
                              )}
                              <Menu.Item
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                  e.stopPropagation();

                                  setMaintenanceTaskIdToDelete(task.maintenanceTaskId);
                                }}
                              >
                                Delete task
                              </Menu.Item>
                            </>
                          }
                        />
                      </Box>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </>
        )}
      </Table.Root>
      {maintenanceTaskIdToDelete && (
        <DeleteTaskModal
          listingAlias={listingAlias}
          fromExternalApp={fromExternalApp}
          clickOriginationPoint={TASKS_TABLE}
          maintenanceTaskId={maintenanceTaskIdToDelete}
          onClose={() => {
            setMaintenanceTaskIdToDelete('');
          }}
        />
      )}
    </>
  );
};
